import { useEffect, useState } from "react";
import { CodeXml } from "lucide-react";

// Local dev only: one-click persona sign-in (eng-docs "AD - Dev Login & Manual-Test Personas"). Renders nothing unless
// the server answers `personasUrl`. Sandbox keeps its own copy (DevLoginPanel.tsx): keep the badge styling in step.

export type DevLoginPersona = {
  key: string;
  label: string;
  email: string;
  organisationName: string | null;
  role: string;
  scope: "Organisation" | "Client" | "Staff";
};

/** The persona list: undefined while loading, null when dev login is off. */
export function useDevLoginPersonas(personasUrl: string): DevLoginPersona[] | null | undefined {
  const [personas, setPersonas] = useState<DevLoginPersona[] | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    fetch(personasUrl)
      .then((resp) => (resp.ok ? resp.json() : null))
      .then((data: unknown) => {
        if (!cancelled) setPersonas(Array.isArray(data) ? (data as DevLoginPersona[]) : null);
      })
      // Off, or the server isn't there.
      .catch(() => {
        if (!cancelled) setPersonas(null);
      });
    return () => {
      cancelled = true;
    };
  }, [personasUrl]);

  return personas;
}

const buttonClass =
  "inline-flex h-8 items-center justify-center rounded-md px-3 text-sm font-medium transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";

export interface DevLoginPanelProps {
  personas: DevLoginPersona[];
  /** Signs in as the persona; resolves to an error message, or null on success. */
  onSignIn: (persona: DevLoginPersona) => Promise<string | null>;
  /** Backend reset of the manual-test organisations. Omit to hide the reset control. */
  resetUrl?: string;
}

export function DevLoginPanel({ personas, onSignIn, resetUrl }: DevLoginPanelProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  if (personas.length === 0) return null;

  const groups = new Map<string, DevLoginPersona[]>();
  for (const persona of personas) {
    const group =
      persona.scope === "Client"
        ? `${persona.organisationName} (client)`
        : persona.organisationName ?? "Dolfin";
    groups.set(group, [...(groups.get(group) ?? []), persona]);
  }

  const signIn = async (persona: DevLoginPersona) => {
    setBusy(persona.key);
    setError(null);
    setNotice(null);
    try {
      setError(await onSignIn(persona));
    } catch {
      // The backend restarting or unreachable: say so and leave the buttons usable.
      setError("Couldn't reach the backend for dev login. Is it running?");
    } finally {
      setBusy(null);
    }
  };

  const reset = async () => {
    if (!resetUrl) return;
    setBusy("reset");
    setError(null);
    try {
      const resp = await fetch(resetUrl, { method: "POST" });
      if (!resp.ok) {
        const problem = await resp.json().catch(() => null);
        setError(problem?.detail ?? "Couldn't reset the manual-test data.");
        return;
      }
      setNotice("Manual-test organisations reset to their seed data.");
    } catch {
      setError("Couldn't reset the manual-test data.");
    } finally {
      setBusy(null);
      setConfirmReset(false);
    }
  };

  return (
    <div className="w-full border-t pt-4 text-left" data-testid="dev-login-panel">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400">
        <CodeXml className="size-3.5" />
        Dev login · local only
      </p>
      <div className="mt-3 flex flex-col gap-4">
        {[...groups.entries()].map(([group, members]) => (
          <div key={group}>
            <p className="mb-1.5 text-sm font-medium">{group}</p>
            <div className="flex flex-wrap gap-1.5">
              {members.map((persona) => (
                <button
                  key={persona.key}
                  type="button"
                  title={persona.email}
                  disabled={busy !== null}
                  onClick={() => signIn(persona)}
                  data-testid={`dev-login-${persona.key}`}
                  className={`${buttonClass} bg-secondary text-secondary-foreground hover:bg-secondary/80`}
                >
                  {busy === persona.key ? "Signing in…" : persona.role}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {resetUrl && (
        <div className="mt-4 flex items-center gap-2">
          {confirmReset ? (
            <>
              <span className="text-xs text-muted-foreground">Delete and reseed both test orgs?</span>
              <button
                type="button"
                disabled={busy !== null}
                onClick={reset}
                className={`${buttonClass} bg-destructive text-white hover:bg-destructive/90`}
              >
                {busy === "reset" ? "Resetting…" : "Reset"}
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => setConfirmReset(false)}
                className={`${buttonClass} hover:bg-accent`}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              className={`${buttonClass} text-muted-foreground hover:bg-accent hover:text-foreground`}
            >
              Reset test data
            </button>
          )}
        </div>
      )}
      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      {notice && <p className="mt-3 text-sm text-muted-foreground">{notice}</p>}
    </div>
  );
}

/** Menubar marker for a dev-login session: names the persona so a test session is never mistaken for a real one. */
export function DevModeBadge({ label }: { label: string }) {
  return (
    <span
      data-testid="dev-login-indicator"
      title={`Dev login · ${label}`}
      className="inline-flex h-7 max-w-64 items-center gap-1.5 rounded-md border border-amber-500/50 bg-amber-500/10 px-2 text-xs font-medium text-amber-700 dark:text-amber-400"
    >
      <CodeXml className="size-3.5 shrink-0" />
      <span className="font-semibold uppercase tracking-wide">Dev</span>
      <span className="truncate">{label}</span>
    </span>
  );
}
